import React, { useState, useEffect } from "react";
import {
  Package,
  RefreshCw,
  Plus,
  Clock,
  Gift,
} from "lucide-react";
import { API_BASE_URL } from "../../utils/apiConfig";
import { api } from "../../services/api";
import Products from "./Products";
import AddEditProductPage from "./AddEditProductPage";
import SimpleBulkImportModal from "./SimpleBulkImportModal";
import QuantityManagement from "./QuantityManagement";
import QuantityHistory from "./QuantityHistory";
import Offers from "./Offers";
import {
  ProductVariant,
  FilterState,
  TabType,
} from "./types";

const ProductsMain: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>("products");

  // Loading states
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Data states
  const [variants, setVariants] = useState<ProductVariant[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false,
  });

  // Filter states
  const [filters, setFilters] = useState<FilterState>({
    searchTerm: "",
    selectedCategory: "",
    selectedStockStatus: "all",
    selectedManufacturer: "all",
  });

  // Debounce search term and reload when pagination or filters change
  useEffect(() => {
    // Only debounce if search term is being used, otherwise load immediately
    const delay = filters.searchTerm ? 500 : 0;
    const timer = setTimeout(() => {
      loadVariants();
    }, delay);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.limit, filters]);

  // Load variants
  const loadVariants = async () => {
    await loadVariantsWithPagination();
  };

  const handlePageChange = (newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleLimitChange = (newLimit: number) => {
    const newPagination = { ...pagination, limit: newLimit, page: 1 };
    setPagination(newPagination);
    // Reload variants immediately with new limit
    loadVariantsWithPagination(newPagination);
  };

  const loadVariantsWithPagination = async (paginationOverride?: typeof pagination) => {
    const currentPagination = paginationOverride || pagination;
    try {
      setInitialLoading(true);
      setError(null);

      console.log("📦 [PRODUCTS] Loading products...", { page: currentPagination.page, limit: currentPagination.limit, filters });
      
      // Build query params
      const params: any = {
        page: currentPagination.page,
        limit: currentPagination.limit,
      };
      
      if (filters.searchTerm) params.search = filters.searchTerm;
      if (filters.selectedCategory) params.category = filters.selectedCategory;
      if (filters.selectedStockStatus !== "all") params.stockStatus = filters.selectedStockStatus;
      if (filters.selectedManufacturer !== "all") params.manufacturer = filters.selectedManufacturer;
      
      const token = localStorage.getItem("authToken");
      const queryString = new URLSearchParams(params).toString();
      const response = await fetch(`${API_BASE_URL}/api/products?${queryString}`, {
        credentials: "include",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      
      const data = await response.json();

      console.log("📦 [PRODUCTS] API Response:", data);

      if (data?.success) {
        let productsData = data.data?.products || [];
        const paginationData = data.data?.pagination || currentPagination;
        
        console.log(`📦 [PRODUCTS] Setting ${productsData.length} products`);
        setVariants(productsData); // Using same state variable for now
        setPagination(paginationData);
      } else {
        console.warn("⚠️ [PRODUCTS] Products response not successful:", data);
        setVariants([]);
      }

      setInitialLoading(false);
    } catch (error) {
      console.error("❌ [PRODUCTS] Error loading products:", error);
      setError("Failed to load products: " + (error instanceof Error ? error.message : String(error)));
      setInitialLoading(false);
    }
  };

  const handleFilterChange = (newFilters: Partial<FilterState>) => {
    setFilters((prev: FilterState) => ({ ...prev, ...newFilters }));
    // Reset to first page when filters change
    setPagination(prev => ({ ...prev, page: 1 }));
    // Reload variants when filters change
    loadVariants();
  };

  const tabs = [
    { id: "products", label: "Products", icon: Package },
    { id: "offers", label: "Offers", icon: Gift },
    { id: "quantity", label: "Quantity", icon: Plus },
    { id: "history", label: "History", icon: Clock },
  ];

  const customButtons = [
    {
      id: "refresh",
      label: "Refresh",
      icon: RefreshCw,
      onClick: () => {
        loadVariants();
      },
      variant: "primary",
    },
  ];


  const renderTabContent = () => {
    switch (activeTab) {
      case "products":
        return (
          <Products
            products={variants as any}
            loading={initialLoading}
            error={error}
            filters={filters}
            pagination={pagination}
            onRefresh={loadVariants}
            onFilterChange={handleFilterChange}
            onPageChange={handlePageChange}
            onLimitChange={handleLimitChange}
          />
        );
      case "offers":
        return <Offers />;
      case "quantity":
        return <QuantityManagement onRefresh={loadVariants} />;
      case "history":
        return <QuantityHistory />;
      default:
        return (
          <div className="flex items-center justify-center h-64">
            <div className="text-gray-500">Select a tab to view content</div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <Package className="w-8 h-8 mr-3 text-[#264a3e]" />
                Products Management
              </h1>
              <p className="mt-2 text-gray-600">
                Manage products, inventory, pricing, and translations
              </p>
            </div>

            {/* Custom Buttons */}
            {customButtons.length > 0 && (
              <div className="flex items-center space-x-3 ml-6">
                {customButtons.map((button) => {
                  const ButtonIcon = button.icon;
                  return (
                    <button
                      key={button.id}
                      onClick={button.onClick}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#264a3e] hover:bg-[#1e3a32] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#264a3e] transition-colors"
                    >
                      {ButtonIcon && <ButtonIcon className="w-4 h-4 mr-2" />}
                      {button.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
              {tabs.map((tab) => {
                const TabIcon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as TabType)}
                    className={`${
                      activeTab === tab.id
                        ? "border-[#264a3e] text-[#264a3e]"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
                  >
                    <TabIcon className="w-4 h-4 mr-2" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6">{renderTabContent()}</div>
        </div>
      </div>
    </div>
  );
};

export default ProductsMain;

